import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Image01Icon } from '@hugeicons/core-free-icons';
import { sanitizeImgSrc } from '@overlayqa/shared';
import { ImageWithLoader } from '@overlayqa/shared/ui';
import { getTypeVisual, IconifyGlyph } from './badges/TypeBadge';
// Relative, not `@/lib/logger`: jest's moduleNameMapper stubs the
// import.meta-reading logger by RELATIVE specifier only (jest.config.js), and
// the `@/` form falls through to the real module, which cannot be imported
// under the CommonJS transform. Same trap the config's 2026-08-17 analytics
// comment documents.
import { log } from '../../lib/logger';

log.info('[IssueThumbnail] Module loaded');

/**
 * One box for both states, so a list of mixed rows keeps a straight edge.
 *
 * `block` is load-bearing, not tidiness. An <img> is inline by default, so it
 * sits on the text baseline and the line box reserves descender space under it;
 * the placeholder <span> below does not. Measured in real Chromium on
 * 2026-08-30, that made rows carrying a screenshot 49px tall against 48px for
 * rows without one — a visibly ragged list. Both states are block-level here so
 * neither participates in baseline layout.
 */
const BOX_BASE = 'rounded border border-gray-200 flex-shrink-0 block';
/**
 * `sm` is the issues-table cell. `md` is the add-scan modal's cell
 * (AddScanIssuesModal): the free checker's element crops are 420x240 context
 * crops with the element outlined in red (measured 2026-09-09 on production
 * rows), and at 40px that outline shrinks to ~15px, a red dot rather than an
 * element. 56x32 keeps the crop's own 7:4 ratio, so object-fit crops nothing.
 */
const SIZES = {
  sm: { box: 'w-10 h-7', width: 40, height: 28 },
  md: { box: 'w-14 h-8', width: 56, height: 32 },
} as const;

interface IssueThumbnailProps {
  /** `screenshotUrl` off the issue. '' is the server's "no screenshot" sentinel. */
  screenshotUrl?: string | null;
  /** Issue type — drives the placeholder glyph when there is no screenshot. */
  type?: string;
  /** Issue title, used to label the image for screen readers. */
  title?: string;
  /** Box size; see SIZES. Defaults to the issues-table cell. */
  size?: keyof typeof SIZES;
}

/**
 * The screenshot preview cell in the project issues table.
 *
 * Three behaviours here are load-bearing, each from a production measurement
 * taken 2026-08-30 rather than from taste:
 *
 *  - LAZY. Stored screenshots are full-size PNGs with no thumbnail variant
 *    (median 47KB, mean 89KB, max 461KB over a 12-row sample) and the list asks
 *    for `pageSize=100`, so the column downloads real page-weight.
 *    MEASURED, not projected: on a 100-row page at 1440x900, Chrome fetched
 *    54 of 75 thumbnails before any scroll and deferred 21 (~28%); the
 *    lazy-stripped control arm fetched all 75 up front and deferred none.
 *    So this saves about a quarter of the column's bytes at this viewport —
 *    worth having and NOT the 5-9MB rescue an earlier version of this comment
 *    claimed, because Chrome's lazy threshold (~1250px past the fold) already
 *    covers most of a 100-row table. The real fix for the rest is a
 *    server-side thumbnail variant, which does not exist yet.
 *  - PLACEHOLDER, NOT A HOLE. 416 of 926 non-demo-seed production issues (45%)
 *    have no screenshot — 51% of `extension` issues and 100% of `mcp` ones. The
 *    cell falls back to the issue's TYPE glyph (Emily's call, 2026-08-30) so it
 *    still carries information, and keeps the same box so rows stay uniform.
 *  - NEVER A BROKEN IMAGE. Six production rows point at a fake `r2.example`
 *    host, and the 2026-08-28 snapshot-rot incident proved stored URLs can 404
 *    later. A failed load degrades to the placeholder instead of the browser's
 *    broken-image glyph, and says so in the console so the rot is observable.
 */
export function IssueThumbnail({ screenshotUrl, type, title, size = 'sm' }: IssueThumbnailProps) {
  const { box, width: WIDTH, height: HEIGHT } = SIZES[size];
  const BOX = `${box} ${BOX_BASE}`;
  // Deliberately NOT run through `resolveAssetUrl` (lib/api/client), unlike
  // ScreenshotCard. Two reasons, in this order:
  //  1. The server already rewrites screenshot URLs to the runtime host on this
  //     exact read path — `withDisplayImageUrls` in issue-query.service.ts:136,
  //     whose own comment is "on the display/read path, point them at the
  //     runtime app host so they resolve in local dev. No-op in production."
  //     So the list payload arrives dev-correct and the client rewrite is a
  //     second belt for a problem already handled upstream.
  //  2. `client.ts` transitively reads `import.meta.env` (apiBaseUrl, analytics,
  //     logger), and apiBaseUrl.ts's own docstring warns that pulling that in
  //     makes a module un-importable under ts-jest's CommonJS transform.
  //     `issuesPageColumns` is imported by several suites that have no business
  //     mocking an API client; importing it here broke two of them outright.
  // If a URL still fails to resolve, onError below degrades to the placeholder
  // rather than painting a broken image.
  const safeUrl = sanitizeImgSrc(screenshotUrl);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  // A row can be recycled onto a different issue (filter change, refetch), so a
  // previous URL's failure must not suppress the new one's image.
  useEffect(() => { setFailedUrl(null); }, [safeUrl]);

  const showImage = safeUrl !== '' && failedUrl !== safeUrl;

  if (!showImage) {
    const visual = getTypeVisual(type);
    return (
      <span
        className={`${BOX} bg-gray-50 flex items-center justify-center`}
        role="img"
        aria-label="No screenshot"
        title="No screenshot"
      >
        {visual?.icon ? (
          <HugeiconsIcon icon={visual.icon} size={14} strokeWidth={2} color="currentColor" className={`w-3.5 h-3.5 ${visual.text}`} aria-hidden="true" />
        ) : visual?.iconifyIcon ? (
          <IconifyGlyph name={visual.iconifyIcon} className={`w-3.5 h-3.5 ${visual.text}`} />
        ) : (
          <HugeiconsIcon icon={Image01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 text-gray-300" aria-hidden="true" />
        )}
      </span>
    );
  }

  return (
    <span className={`relative ${BOX}`}>
    <ImageWithLoader fill compact
      src={safeUrl}
      alt={title ? `Screenshot of ${title}` : 'Issue screenshot'}
      width={WIDTH}
      height={HEIGHT}
      // Explicit dimensions reserve the box before the bytes land, so a page of
      // rows does not jump as screenshots stream in. Anchored top-LEFT: an
      // element crop of a heading is a wide strip with its text at the left
      // edge (304x27 on the Orbitly review, 2026-10-19), and centring it showed
      // only the strip's empty middle. A tall crop still shows its top.
      className="w-full h-full object-cover object-left-top bg-gray-50"
      loading="lazy"
      decoding="async"
      onError={() => {
        log.warn('[IssueThumbnail] Screenshot failed to load', { url: safeUrl });
        setFailedUrl(safeUrl);
      }}
    />
    </span>
  );
}
